import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AvatarUpload, Button, Input, SparkIcon, sparkToast, useReadOnly } from '@spark';

import SettingsSectionCard from '@/features/settings/components/SettingsSectionCard.jsx';
import { USERS_TAB_ID } from '@/features/settings/components/settingsTabs';
import { useAuth } from '@/features/auth/AuthContext';
import { deleteWorkspace, updateWorkspace } from '../../api/workspacesApi';
import { LOGO_ACCEPT, cropImageToSquare, logoImageErrorText } from '../../utils/logoImage';
import { sanitizeWorkspaceName } from '../../utils/workspaceName';
import useLogoCrop from '../../hooks/useLogoCrop';
import LogoCropModal from '../LogoCropModal';
import DeleteWorkspaceModal from './DeleteWorkspaceModal';
import TransferOwnershipModal from './TransferOwnershipModal';
import WorkspaceDeletedView from './WorkspaceDeletedView';
import WorkspaceReadOnlyView from './WorkspaceReadOnlyView';
import '@/features/settings/components/settings.css';
import './WorkspaceSettingsTab.css';

const NAME_MAX_LENGTH = 200;
export const DANGER_ZONE_TITLE = 'Danger Zone';

/**
 * Settings > Workspace (Figma 9244:4940) is every member's landing tab.
 * Editors and Viewers get `WorkspaceReadOnlyView` (name, owner, their role,
 * member count); Admins and gods edit the active workspace's logo and name.
 * Both edits autosave through ``PATCH /workspaces/{id}``
 * — the logo once the user has framed it in `LogoCropModal`, the name on
 * blur / Enter when it is non-blank and differs from the saved value — and
 * the session is patched in place so the header and avatar menu refresh
 * without a reload.  The name field never locks while saving (a "Saving…"
 * helper and `aria-busy` show the state instead), so typing is not
 * interrupted by a slow PATCH.  A name another workspace already uses comes
 * back as 409 and is shown under the field — the rejected draft stays put so
 * it can be edited.
 *
 * The **Danger Zone** below the fields is rendered only for the workspace
 * owner (`workspace.is_owner`): "Select New Owner" opens
 * `TransferOwnershipModal` (`POST /workspaces/{id}/transfer-ownership`) and
 * patches the session so the zone disappears once ownership has moved — or,
 * when there is nobody to hand over to, its "Invite" button switches to the
 * User Management tab (``?tab=users``, picked up by ``Settings``);
 * "Delete Workspace" opens `DeleteWorkspaceModal` (typed-name confirmation)
 * and, after `DELETE /workspaces/{id}` succeeds, replaces the page with
 * `WorkspaceDeletedView` — "Continue" into the first remaining workspace or
 * "Go to website" when none are left.
 *
 * `cropLogo` is injectable so tests can skip the canvas pipeline.
 */
const WorkspaceSettingsTab = ({ cropLogo = cropImageToSquare }) => {
    const { workspace, role, platformRole, isAuthEnabled, applyWorkspaceUpdate, switchWorkspace } = useAuth();
    const { readOnly } = useReadOnly();
    const [searchParams, setSearchParams] = useSearchParams();
    const canEdit = !isAuthEnabled || role === 'Admin' || platformRole === 'god';
    const disabled = readOnly || !canEdit || !workspace?.id;
    const isOwner = Boolean(isAuthEnabled && workspace?.is_owner);

    const [transferOpen, setTransferOpen] = useState(false);

    const goToUserManagement = () => {
        setTransferOpen(false);
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('tab', USERS_TAB_ID);
        setSearchParams(nextParams);
    };
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    // Set once the workspace is gone: `{ remaining: [...] }` swaps the page for the success view.
    const [deleted, setDeleted] = useState(null);
    const [continuing, setContinuing] = useState(false);

    const savedName = workspace?.name || '';
    const [name, setName] = useState(savedName);
    const [nameError, setNameError] = useState('');
    const [savingName, setSavingName] = useState(false);
    const [savingLogo, setSavingLogo] = useState(false);
    // The field stays editable while a rename is in flight, so a later save
    // can overtake an earlier one: each save takes a sequence number and only
    // the newest one is allowed to toast or patch the session.  `pendingName`
    // stops Enter + the blur that follows from sending the same value twice.
    // PATCHes are also sent one at a time (`nameSaveChain`): a draft that was
    // superseded while it waited is never sent, so the server always ends up
    // with the newest name rather than whichever response happened to land
    // last.
    const nameSaveSeq = useRef(0);
    const nameSaveChain = useRef(Promise.resolve());
    const pendingName = useRef(null);
    const lastSavedName = useRef(savedName);
    const lastWorkspaceId = useRef(workspace?.id);

    // Follow the session (another tab renamed it, workspace switched, save
    // confirmed) — but never over a draft the user is still typing: a rename
    // confirmed mid-keystroke must not wipe what came after it.
    useEffect(() => {
        const switched = lastWorkspaceId.current !== workspace?.id;
        lastWorkspaceId.current = workspace?.id;
        if (switched) setNameError('');
        const previous = lastSavedName.current;
        lastSavedName.current = savedName;
        setName((draft) => {
            const untouched = draft.trim() === previous || draft.trim() === savedName;
            return switched || untouched ? savedName : draft;
        });
    }, [workspace?.id, savedName]);

    const applyResult = (result, successMessage) => {
        if (!result.ok) {
            sparkToast.error(result.error);
            return false;
        }
        if (result.data?.workspace) {
            applyWorkspaceUpdate(result.data.workspace);
        }
        sparkToast.success(successMessage);
        return true;
    };

    const saveName = async () => {
        if (disabled) return;
        // An untouched draft (possibly a legacy name with spaces) is never
        // re-saved; an edited one follows the dash rule like task names.
        const trimmed = name === savedName ? savedName : sanitizeWorkspaceName(name, { trimEnd: true }).trim();
        if (!trimmed) {
            // Blank is never saved; snap back to what the server has.
            setName(savedName);
            setNameError('');
            return;
        }
        if (trimmed === savedName || trimmed === pendingName.current) {
            if (trimmed !== name) setName(trimmed);
            return;
        }
        nameSaveSeq.current += 1;
        const seq = nameSaveSeq.current;
        pendingName.current = trimmed;
        // Show exactly what is being saved (no trailing dash).
        if (trimmed !== name) setName(trimmed);
        setNameError('');
        setSavingName(true);
        const workspaceId = workspace.id;
        const run = async () => {
            // Superseded while waiting for the previous PATCH: never send it.
            if (seq !== nameSaveSeq.current) return;
            try {
                const result = await updateWorkspace(workspaceId, { name: trimmed });
                // A newer rename superseded this one; its response is the one that counts.
                if (seq !== nameSaveSeq.current) return;
                // A taken name is shown under the field — the draft stays so the
                // user can edit it instead of retyping after a toast disappears.
                if (!result.ok && result.nameTaken) {
                    setNameError(result.error);
                    return;
                }
                applyResult(result, 'Workspace name updated');
            } finally {
                if (seq === nameSaveSeq.current) {
                    pendingName.current = null;
                    setSavingName(false);
                }
            }
        };
        const turn = nameSaveChain.current.then(run);
        // Keep the chain alive after a rejected save so later renames still go out.
        nameSaveChain.current = turn.catch(() => {});
        await turn;
    };

    const handleNameKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            saveName();
        } else if (event.key === 'Escape') {
            setName(savedName);
            setNameError('');
        }
    };

    const saveLogo = async (logo) => {
        if (disabled || !logo) return;
        setSavingLogo(true);
        try {
            const result = await updateWorkspace(workspace.id, { logo });
            applyResult(result, 'Workspace logo updated');
        } finally {
            setSavingLogo(false);
        }
    };

    // Picked file → crop dialog → `saveLogo(dataUrl)`; an unsupported file or
    // a crop failure toasts and keeps the current logo.
    const logoCrop = useLogoCrop({
        onLogo: saveLogo,
        onError: (err) => sparkToast.error(logoImageErrorText(err)),
    });

    const handleTransferred = ({ newOwnerEmail } = {}) => {
        setTransferOpen(false);
        if (workspace?.id) {
            applyWorkspaceUpdate({ id: workspace.id, owner_email: newOwnerEmail, is_owner: false });
        }
    };

    const confirmDelete = async () => {
        if (!workspace?.id || deleting) return;
        setDeleting(true);
        const result = await deleteWorkspace(workspace.id);
        setDeleting(false);
        if (!result.ok) {
            sparkToast.error(result.error || 'Could not delete the workspace.');
            return;
        }
        setDeleteOpen(false);
        setDeleted({ remaining: result.data?.remaining_workspaces || [] });
    };

    // "Continue" after deletion: make the first remaining workspace active on
    // the server, then reload from the root so every workspace-scoped cache
    // (projects, datasets, billing) starts fresh.
    const continueInto = async (next) => {
        if (!next?.id || continuing) return;
        setContinuing(true);
        try {
            await switchWorkspace(next.id);
            window.location.assign('/');
        } catch (err) {
            setContinuing(false);
            sparkToast.error(err?.message || 'Could not switch workspace.');
        }
    };

    if (deleted) {
        return <WorkspaceDeletedView remainingWorkspaces={deleted.remaining} onContinue={continueInto} busy={continuing} />;
    }

    // Editors / Viewers: a summary instead of a form full of disabled controls.
    if (!canEdit) {
        return <WorkspaceReadOnlyView workspace={workspace} role={role} />;
    }

    return (
        <SettingsSectionCard iconSlug="workspace" title="Workspace">
            <div className="settings-providers__panel">
                <h3 className="settings-subheading fs-unmask">Manage Workspace</h3>

                <div className="workspace-settings__fields">
                    <AvatarUpload
                        label="Workspace Image"
                        emptyVariant="initials"
                        initials={savedName}
                        src={workspace?.logo || null}
                        alt="Workspace logo"
                        accept={LOGO_ACCEPT}
                        disabled={disabled || savingLogo}
                        onFileSelected={logoCrop.pickLogo}
                    />
                    <LogoCropModal {...logoCrop.modalProps} cropLogo={cropLogo} />

                    <Input
                        id="workspace-settings-name"
                        className="workspace-settings__name"
                        size="medium"
                        label="Workspace Name"
                        placeholder="Name of company workspace"
                        value={name}
                        maxLength={NAME_MAX_LENGTH}
                        disabled={disabled}
                        aria-busy={savingName || undefined}
                        danger={Boolean(nameError)}
                        showHelperText={savingName || Boolean(nameError)}
                        helperText={nameError || 'Saving…'}
                        onChange={(event) => {
                            // Like task names: a typed (or pasted) space becomes a dash.
                            setName(sanitizeWorkspaceName(event.target.value));
                            if (nameError) setNameError('');
                        }}
                        onBlur={saveName}
                        onKeyDown={handleNameKeyDown}
                        autoComplete="organization"
                    />
                </div>

                {isOwner ? (
                    <section className="workspace-settings__danger-zone" aria-labelledby="workspace-danger-zone-title">
                        <h4 id="workspace-danger-zone-title" className="workspace-settings__danger-heading fs-unmask">
                            {DANGER_ZONE_TITLE}
                        </h4>
                        <div className="workspace-settings__danger-action">
                            <span className="workspace-settings__danger-label">
                                Transfer ownership to another teammate
                            </span>
                            <Button
                                type="button"
                                variant="secondary"
                                size="medium"
                                disabled={readOnly}
                                startIcon={<SparkIcon slug="user" size="medium" />}
                                onClick={() => setTransferOpen(true)}
                            >
                                Select New Owner
                            </Button>
                        </div>
                        <div className="workspace-settings__danger-action">
                            <span className="workspace-settings__danger-label">Delete workspace forever</span>
                            <Button
                                type="button"
                                variant="secondary"
                                danger
                                size="medium"
                                disabled={readOnly}
                                startIcon={<SparkIcon slug="trash" size="medium" />}
                                onClick={() => setDeleteOpen(true)}
                            >
                                Delete Workspace
                            </Button>
                        </div>
                    </section>
                ) : null}
            </div>

            {isOwner ? (
                <>
                    <TransferOwnershipModal
                        open={transferOpen}
                        workspaceId={workspace?.id}
                        workspaceName={savedName}
                        onClose={() => setTransferOpen(false)}
                        onTransferred={handleTransferred}
                        onInvite={goToUserManagement}
                    />
                    <DeleteWorkspaceModal
                        open={deleteOpen}
                        workspaceName={savedName}
                        busy={deleting}
                        onClose={() => setDeleteOpen(false)}
                        onConfirm={confirmDelete}
                    />
                </>
            ) : null}
        </SettingsSectionCard>
    );
};

export default WorkspaceSettingsTab;
